import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddCircle, ChevronRight } from "@mui/icons-material";
// import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
// import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
// import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import { ReactComponent as PlanIcon } from '../icons/PlanIcon.svg'
import { ReactComponent as ReservationIcon } from '../icons/ReservationIcon.svg'
import { ReactComponent as MessageIcon } from '../icons/MessageIconSelected.svg'
import { ReactComponent as MembershipIcon } from '../icons/MembershipIcon.svg'
import { ReactComponent as ResourcesIcon } from '../icons/ResourcesIcon.svg'


import { PreferenceSchema, ResourceCategoryTypes, ResourceSchema, ResourceType } from "../data/types";
import {
  useLoadCommunityBookings,
  useLoadMemberships,
  useLoadMessages,
  useLoadRequests,
} from "../hooks/memberhooks";
import { useLoadCommunityResourcesOnly, useLoadResourceOptions } from "../hooks/resourcehooks";
import { useResourceState } from "../contexts/kitchencontext";
import { computeDashboardStats, getDashboardResourceOptions } from "../utils/helpers/dashboardStats";
import { styleThousandCommas, capitalizeString } from "../utils/helpers";
import { ReactComponent as KitchenIcon } from "../icons/KitchenIcon.svg";
import type { DashboardStats } from "../utils/helpers/dashboardStats";

type CommunityDataProps = {
  preferences?: PreferenceSchema;
};

type StatCardProps = {
  title: string;
  icon: React.ReactNode;
  metric: React.ReactNode;
  actionLabel: string;
  onAction: () => void;
  badge?: React.ReactNode;
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  icon,
  metric,
  actionLabel,
  onAction,
  badge,
}) => (
  <div className="page-card flex min-h-[210px] flex-col p-5">
    <div className="flex flex-row justify-between items-start">
      <div className="flex h-[48px] w-[48px] rounded-full bg-[#EFF8FA] items-center justify-center">
        {icon}
      </div>
      {badge}
    </div>
    <p className="text-[20px] font-raleway font-medium text-black mt-4">{title}</p>
    <div className="text-[16px] font-open-sans font-thin text-[#4D4D4D] mt-2 flex-1">{metric}</div>
    <button
      onClick={onAction}
      className="mt-4 flex flex-row items-center justify-center gap-1 border border-[#E9E9E9] rounded-md py-2.5 text-primary-200 font-raleway text-[16px] font-semibold hover:bg-[#F9FAFB]"
    >
      {actionLabel}
      <ChevronRight style={{ fontSize: 18 }} />
    </button>
  </div>
);

const ChangeBadge = ({
  value,
  formatValue,
  isPercentage = false,
}: {
  value: number;
  formatValue: (amount: number) => string;
  isPercentage?: boolean;
}) => {
  if (value === 0) return null;

  const isPositive = value > 0;
  const prefix = (isPositive && !isPercentage) ? "+" : "";
  const displayValue = isPercentage
    ? `${prefix}${value}%`
    : `${prefix}${formatValue(Math.abs(value))}`;

  return (
    <div className="flex flex-row items-center">
    <span
      className={`text-[14px] font-raleway font-semibold px-3 py-1.5 rounded-full ${
        isPositive ? "bg-[#E8F8EE] text-[#0F973D]" : "bg-[#FDECEC] text-[#D14343]"
      }`}
    >
      {displayValue}
    </span>
    <span className="text-[#808080] text-[14px] italic font-thin font-open-sans ml-2">vs last month</span>
    </div>
  );
};

const MembershipStatMetric = ({
  stats,
  emphasized = false,
}: {
  stats: DashboardStats;
  emphasized?: boolean;
}) => {
  const valueClass = emphasized
    ? "font-semibold text-[#013847] font-raleway text-[18px]"
    : "font-bold text-black";

  if (stats.pendingMembershipRequests > 0) {
    return (
      <>
        <span className={valueClass}>{stats.pendingMembershipRequests}</span> pending membership
        requests need your approval
      </>
    );
  }

  if (stats.mostUsedTier) {
    return (
      <>
        <span className={emphasized ? "font-semibold text-primary-200 font-raleway text-[18px]" : "font-bold text-primary-200"}>
          {stats.mostUsedTier.name}
        </span>{" "}
        is your most used tier with{" "}
        <span className={valueClass}>{stats.mostUsedTier.count}</span> active members
      </>
    );
  }

  return (
    <>
      <span className={valueClass}>{stats.activeMembers}</span> active members across your
      community
    </>
  );
};

const CommunityData: React.FC<CommunityDataProps> = ({ preferences }) => {
  const navigate = useNavigate();
  const { communityId } = useResourceState();
  const { resources: allResources = [], isLoadingResources } = useLoadCommunityResourcesOnly(
    communityId || undefined
  );
  const resourceOptions = useMemo(
    () => getDashboardResourceOptions(preferences, allResources),
    [preferences, allResources]
  );
  const [currentOption, setCurrentOption] = useState<ResourceCategoryTypes | undefined>(
    resourceOptions[0]
  );

  const activeOption =
    resourceOptions.find((option) => option.type === currentOption?.type) ||
    resourceOptions[0];
  const activeType: ResourceType = activeOption?.type ?? "kitchen";

  const { bookings = [] } = useLoadCommunityBookings(activeType);
  const { messages = [] } = useLoadMessages();
  const { memberships = [] } = useLoadMemberships();
  const { membershipRequests = [] } = useLoadRequests();
  const { getResourceIconData } = useLoadResourceOptions();
  const resourceIconData = getResourceIconData(activeType);
  const ResourceIcon = resourceIconData.Icon;

  const stats = useMemo(
    () => computeDashboardStats(bookings, messages, memberships, membershipRequests),
    [bookings, messages, memberships, membershipRequests]
  );

  const formatCurrency = (amount: number) => `$${styleThousandCommas(Math.round(amount))}`;
  const filteredResources = useMemo(
    () =>
      (allResources as ResourceSchema[]).filter(
        (resource) => (resource.resource_type || "kitchen") === activeType
      ),
    [allResources, activeType]
  );
  const hasResources = filteredResources.length > 0;
  const resourceLabel =
    activeType === "kitchen" ? "Kitchen Space" : activeOption?.name || "resource";
  const navigateToResourceBookings = () =>
    navigate(`/resources/${activeType}?view=bookings`);
  const navigateToMembershipMembers = () =>
    navigate("/memberships?view=members");
  const navigateToResourceOfferSettings = () => navigate("/settings");

  const kitchenCards = (
    <>
      <StatCard
        title="Today's Bookings"
        icon={<ReservationIcon className="text-primary-400" />}
        metric={
          <>
            <span className="font-semibold text-[#013847] font-raleway text-[18px]">{stats.todayBookings}</span> bookings across{" "}
            <span className="font-semibold text-[#4D4D4D]">{stats.todayResourceCount}</span>{" "}
            {stats.todayResourceCount === 1 ? "kitchen" : "kitchens"} today
          </>
        }
        actionLabel="View Bookings"
        onAction={navigateToResourceBookings}
      />
      <StatCard
        title="Membership Requests"
        icon={<MembershipIcon className="h-[24px] w-[24px] text-primary-400" />}
        metric={<MembershipStatMetric stats={stats} emphasized />}
        actionLabel="View Members"
        onAction={navigateToMembershipMembers}
      />
      <StatCard
        title="Earnings This Month"
        icon={<PlanIcon className="text-primary-400 h-[24px] w-[24px]" />}
        badge={
          <ChangeBadge value={stats.earningsChange} formatValue={formatCurrency} />
        }
        metric={
          <>
            <span className="font-semibold text-[#013847] font-raleway text-[18px]">{formatCurrency(stats.earningsThisMonth)}</span>{" "}
            earned this month
          </>
        }
        actionLabel="View Earnings"
        onAction={() => navigate(`/payments/${activeType}`)}
      />
      <StatCard
        title="Completion Rate"
        icon={<ResourcesIcon className="text-primary-400" />}
        badge={
          <ChangeBadge
            value={stats.bookingRateChange}
            formatValue={(amount) => `${amount}%`}
            isPercentage
          />
        }
        metric={
          <>
            <span className="font-semibold text-[#013847] font-raleway text-[18px]">{stats.bookingRate}%</span> acceptance rate on{" "}
            <span className="font-semibold text-[#4D4D4D]">{stats.totalRequestsThisMonth}</span> requests
          </>
        }
        actionLabel="View More"
        onAction={navigateToResourceBookings}
      />
      <StatCard
        title="Unread Messages"
        icon={<MessageIcon className="text-primary-400" />}
        metric={
          <>
            <span className="font-semibold text-[#013847] font-raleway text-[18px]">{stats.unreadMessages}</span> unread messages
            from users
          </>
        }
        actionLabel="View Messages"
        onAction={() => navigate("/messages")}
      />
      <StatCard
        title="Most Booked Kitchen"
        icon={<KitchenIcon className="h-[22px] w-[22px] text-primary-400" />}
        metric={
          stats.mostBookedResource ? (
            <>
              <span className="font-semibold text-primary-200 font-raleway text-[18px]">{stats.mostBookedResource.name}</span>{" "}
              booked{" "}
              <span className="font-semibold text-[#4D4D4D]">{stats.mostBookedResource.count}</span> times
              this month
            </>
          ) : (
            <>No bookings recorded this month yet</>
          )
        }
        actionLabel="View More"
        onAction={navigateToResourceBookings}
      />
    </>
  );

  const genericCards = (
    <>
      <StatCard
        title="Today's Requests"
        icon={<ReservationIcon className="text-primary-400" />}
        metric={
          <>
            <span className="font-semibold text-primary-200 font-raleway text-[18px]">{stats.todayBookings}</span> requests across{" "}
            <span className="font-semibold text-[#4D4D4D]">{stats.todayResourceCount}</span>{" "}
            {stats.todayResourceCount === 1 ? "resource" : "resources"} today
          </>
        }
        actionLabel="View Requests"
        onAction={navigateToResourceBookings}
      />
      <StatCard
        title="Membership Requests"
        icon={<MembershipIcon className="h-[24px] w-[24px] text-primary-400" />}
        metric={<MembershipStatMetric stats={stats} />}
        actionLabel="View Members"
        onAction={navigateToMembershipMembers}
      />
      <StatCard
        title="Earnings This Month"
        icon={<PlanIcon className="text-primary-400 h-[24px] w-[24px]" />}
        badge={
          <ChangeBadge value={stats.earningsChange} formatValue={formatCurrency} />
        }
        metric={
          <>
            <span className="font-semibold text-primary-200 font-raleway text-[18px]">{formatCurrency(stats.earningsThisMonth)}</span>{" "}
            earned this month
          </>
        }
        actionLabel="View Earnings"
        onAction={() => navigate(`/payments/${activeType}`)}
      />
      <StatCard
        title="Completion Rate"
        icon={<ResourcesIcon className="text-primary-400" />}
        badge={
          <ChangeBadge
            value={stats.bookingRateChange}
            formatValue={(amount) => `${amount}%`}
            isPercentage
          />
        }
        metric={
          <>
            <span className="font-semibold text-primary-200 font-raleway text-[18px]">{stats.bookingRate}%</span> acceptance rate on{" "}
            <span className="font-semibold text-[#4D4D4D]">{stats.totalRequestsThisMonth}</span> requests
          </>
        }
        actionLabel="View More"
        onAction={navigateToResourceBookings}
      />
      <StatCard
        title="Unread Messages"
        icon={<MessageIcon className="text-primary-400" />}
        metric={
          <>
            <span className="font-bold text-black">{stats.unreadMessages}</span> unread messages
            from users
          </>
        }
        actionLabel="View Messages"
        onAction={() => navigate("/messages")}
        
      />
      <StatCard
        title={`Most Popular ${capitalizeString(activeType)}`}
        icon={<ResourceIcon className="h-[22px] w-[22px] text-primary-400" />}
        metric={
          stats.mostBookedResource ? (
            <>
              <span className="font-bold text-primary-200">{stats.mostBookedResource.name}</span>{" "}
              requested{" "}
              <span className="font-bold text-black">{stats.mostBookedResource.count}</span> times
              this month
            </>
          ) : (
            <>No requests recorded this month yet</>
          )
        }
        actionLabel="View More"
        onAction={navigateToResourceBookings}
      />
    </>
  );

  if (isLoadingResources) {
    return (
      <div className="flex flex-col bg-white mt-4 w-full rounded-2xl border border-[#E9E9E9]">
        <div className="px-6 py-16 text-center text-[#4D4D4D] font-open-sans">
          Loading community data...
        </div>
      </div>
    );
  }

  if (resourceOptions.length === 0) {
    return (
      <div className="flex flex-col bg-white mt-4 w-full rounded-2xl border border-[#E9E9E9]">
        <div className="flex flex-col items-center justify-center px-6 py-16">
          <div className="flex h-[80px] w-[80px] rounded-full bg-[#EFF8FA] items-center justify-center">
            <ResourceIcon className="h-[40px] w-[40px] text-primary-400" />
          </div>
          <p className="text-[20px] text-primary-200 font-raleway font-bold mt-4">
            Choose resources to offer
          </p>
          <p className="text-[16px] font-open-sans text-[#333333] mt-2 text-center max-w-md">
            Select the resource types you offer in Settings to start adding listings and viewing community data.
          </p>
          <button
            onClick={navigateToResourceOfferSettings}
            className="flex flex-row items-center justify-center gap-2 bg-primary-200 text-white border-0 px-6 py-3 rounded-md mt-5 font-raleway text-[16px] font-medium"
          >
            Open Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white mt-4 w-full rounded-2xl border border-[#E9E9E9]">
      <div className="flex flex-row flex-wrap gap-2 px-6 py-5 border-b border-[#E9E9E9]">
        {resourceOptions.map((option) => (
          <button
            key={option.type}
            onClick={() => setCurrentOption(option)}
            className={`${
              activeType === option.type
                ? "text-white bg-primary-200 font-medium"
                : "text-[#4D4D4D] bg-white"
            } text-[16px] font-raleway border border-[#E9E9E9] py-2 px-4 rounded-md cursor-pointer`}
          >
            {option.name}
          </button>
        ))}
      </div>

      {!hasResources ? (
        <div className="flex flex-col items-center justify-center px-6 py-16">
          <div className="flex h-[80px] w-[80px] rounded-full bg-[#EFF8FA] items-center justify-center">
            <ResourceIcon className="h-[40px] w-[40px] text-primary-400" />
          </div>
          <p className="text-[20px] text-primary-200 font-raleway font-bold mt-4">
            Add Your First {resourceLabel}
          </p>
          <p className="text-[16px] font-open-sans text-[#333333] mt-2 text-center max-w-md">
            To view {(activeOption?.name || resourceLabel).toLowerCase()} data you need to set up a{" "}
            {resourceLabel.toLowerCase()}.
          </p>
          <button
            onClick={() => navigate(`/resources/${activeType}`)}
            className="flex flex-row items-center justify-center gap-2 bg-primary-200 text-white border-0 px-6 py-3 rounded-md mt-5 font-raleway text-[16px] font-medium"
          >
            <AddCircle />
            Add {resourceLabel}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 px-6 py-6">
          {activeType === "kitchen" ? kitchenCards : genericCards}
        </div>
      )}
    </div>
  );
};

export default CommunityData;
